/**
 * A setting that lives as a checkbox on the pause screen.
 *
 * There are two of these now - the 18+ switch and whether you keep your things
 * when you die - and they need exactly the same three careful bits: read the
 * saved value without letting blocked storage break the game, write it back
 * the same way, and stop the click reaching the overlay, which turns any click
 * into "start playing" and would begin the game the moment you reached for the
 * box.
 *
 * Generic over what the setting *is*, so the 18+ switch keeps storing the word
 * "adult" and does not have to be migrated to a boolean to share this.
 *
 * The elements are handed in rather than looked up here. That is not style:
 * the style guard finds the ids a UI asks for by reading `getElementById("...")`
 * out of the source, and the first version of this class took the ids as
 * strings - which hid them from it, and deleting a checkbox from index.html
 * stopped being caught.
 */

export class OverlayToggle<T> {
  private current: T;
  private readonly box: HTMLInputElement;

  constructor(
    box: HTMLInputElement,
    /** The label around it. Its clicks must not reach the overlay. */
    row: HTMLElement,
    private storageKey: string,
    /** Turn what was stored - or nothing - into a value. Must accept null. */
    private readonly parse: (raw: string | null) => T,
    /** Turn a value back into a string, and into a tick or an empty box. */
    private readonly write: (value: T) => { stored: string; checked: boolean },
    /** What a tick and an empty box mean. */
    private readonly fromCheckbox: (checked: boolean) => T,
    /** Runs after every change. */
    private readonly onChange: () => void,
  ) {
    this.box = box;

    let saved: string | null = null;
    try {
      saved = localStorage.getItem(storageKey);
    } catch {
      // Private mode or blocked storage. Whatever parse() makes of null is the
      // default, and every setting here has a safe one.
    }
    this.current = parse(saved);
    this.box.checked = write(this.current).checked;

    this.box.addEventListener("change", () => {
      this.set(this.fromCheckbox(this.box.checked));
    });
    // The overlay turns any click into a request for pointer lock, which would
    // start the game the moment you reached for the checkbox.
    row.addEventListener("click", (e) => e.stopPropagation());
  }

  get value(): T {
    return this.current;
  }

  /**
   * Point it at a different setting and show what that one says.
   *
   * For the switches that belong to a map rather than to the game: choosing
   * a кремль and choosing a hill are choosing two different sets of these,
   * and the box has to say which one you are looking at the moment you look.
   *
   * @param fallback where to take the value from when the new key has none.
   * Used once, to carry over the single set everybody had before these
   * belonged to maps - without it, changing map the first time would have
   * quietly turned everybody's switches off.
   */
  retarget(key: string, fallback?: string): void {
    this.storageKey = key;
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(key);
      if (saved === null && fallback !== undefined) {
        saved = localStorage.getItem(fallback);
      }
    } catch {
      // Blocked storage. The default is whatever parse() makes of null.
    }
    this.current = this.parse(saved);
    this.box.checked = this.write(this.current).checked;
  }

  private set(value: T): void {
    this.current = value;
    try {
      localStorage.setItem(this.storageKey, this.write(value).stored);
    } catch {
      // Not remembering the choice is survivable; refusing it is not.
    }
    this.onChange();
  }
}
